import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import ProductCartIcon from '../../../../../assets/Icons/ProductCartIcon';
import { localCart } from '../../../../atoms/LocalCart';
import { localUserID, user } from '../../../../atoms/user';
import {
  addToCart,
  addToRecentlyViewed,
  getCartCount,
} from '../../../../services/Cart';
import { AddToCartProps } from '../../../../utils/interfaces/cart';
import { BookItems } from '../../../../utils/interfaces/Products/books';
import { User } from '../../../../utils/interfaces/User';
import ButtonDefault, {
  ButtonVariants,
  Spinner,
} from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../../shared/formateErrorObject';
import { notify } from '../../../shared/notify';

interface ProductDetailsCardProps {
  productData: BookItems;
  isCourses?: boolean;
}

const CoursesCard: React.FC<ProductDetailsCardProps> = ({ productData }) => {
  const router = useRouter();
  const screens = useBreakpoint();
  const userData: User | undefined = useRecoilValue(user);

  const [loading, setLoading] = useState({ value: false, id: 0 });
  const localUserId = useRecoilValue(localUserID);
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [, setLocalUserId] = useRecoilState<string>(localUserID);

  const handleAddDataToCart = async (payload: AddToCartProps) => {
    try {
      setLoading({ value: true, id: payload.id });
      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      handleCartCount();
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading({ value: false, id: payload.id });
    }
  };

  const handleCartCount = async () => {
    try {
      const data = await getCartCount({});
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  useEffect(() => {
    const setUserIdInLocalStorage = async () => {
      const existingUserId = localStorage.getItem('userId');

      if (!existingUserId) {
        const userId = uuidv4();

        localStorage.setItem('userId', userId);
        setLocalUserId(userId);
      }
      setLocalUserId(existingUserId as string);
    };

    setUserIdInLocalStorage();
  }, []);

  return (
    <div className='w-full rounded-md cursor-pointer flex flex-col transition-duration-300'>
      <div className='h-full text-fadedWhite flex flex-col'>
        <div className='flex flex-col gap-y-4 items-center h-full gap-x-6'>
          <div className='flex justify-center w-full'>
            <div
              className='w-full flex justify-center h-full'
              style={{
                display: 'flex',
                aspectRatio: '2 / 1',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Image
                src={productData?.bookImage}
                alt={productData?.heading}
                className='object-cover rounded-md md:rounded-lg'
                style={{
                  width: '100%',
                  height: '100%',
                }}
                fill
              />
            </div>
          </div>

          <div className='h-full w-full'>
            <div className='flex justify-between md:flex-col gap-y-3 lg:flex-row items-center h-full w-full  gap-x-3'>
              <div className='flex flex-col gap-y-1 h-full w-full justify-center'>
                <div className='text-sm max-w-[230px] md:max-w-full lg:max-w-[290px] xl:max-w-[190px] md:text-base truncate    lg:text-lg font-bold'>
                  {productData.heading}
                </div>
                <ShouldRender check={productData?.is_available}>
                  <span className='flex gap-x-2 items-baseline pl-1'>
                    <p className='line-through text-[#77777C] text-sm md:text-base font-semibold whitespace-nowrap'>
                      {productData?.currency} {productData?.actualPriceHardCopy}
                    </p>
                    <p className='text-sm md:text-base font-bold whitespace-nowrap'>
                      {productData?.currency} {productData?.hardCopyPrice}
                    </p>
                    <ShouldRender check={screens.xl || !screens.lg}>
                      {productData?.actualPriceHardCopy &&
                      productData?.hardCopyPrice ? (
                        <p className='flex gap-x-1 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                          {Math.round(
                            ((productData.actualPriceHardCopy -
                              Number(productData.hardCopyPrice)) /
                              productData.actualPriceHardCopy) *
                              100,
                          )}
                          %{' '}
                          <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                        </p>
                      ) : null}
                    </ShouldRender>
                  </span>
                </ShouldRender>
              </div>
              <ShouldRender check={screens.md}>
                <div className='flex h-fit w-full lg:w-fit'>
                  <ButtonDefault
                    size={1}
                    isWidthFull={true}
                    rounded={false}
                    roundedSm={true}
                    variant={ButtonVariants.WHITE}
                    enableScaling={false}
                    available={productData?.is_available}
                    loading={
                      productData.hardCopyId === loading.id && loading.value
                    }
                    disabled={
                      productData.hardCopyId === loading.id && loading.value
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddDataToCart({
                        id: productData.hardCopyId as number,
                        temp_user_id: localUserId,
                        quantity: 1,
                        trackingData: {
                          productData: {
                            price: Number(productData.softCopyPrice),
                            id: productData.hardCopyId as number,
                            heading: productData.heading as string,
                            quantity: 1,
                            currency: productData?.i18n_attributes
                              ?.currency as string,
                          },
                          userData,
                          pathname: router.pathname,
                          productType: 'hardcopy',
                          type: 'courses',
                        },
                      });
                    }}
                    className='w-full text-center text-sm group whitespace-nowrap'
                  >
                    <span className='text-sm font-semibold lg:py-1 px-3 xl:px-5'>
                      Add to Cart
                    </span>
                  </ButtonDefault>
                </div>
              </ShouldRender>

              <ShouldRender check={!screens.md}>
                <ButtonDefault
                  size={0}
                  isWidthFull={true}
                  rounded={false}
                  roundedSm={true}
                  variant={ButtonVariants.WHITE}
                  enableScaling={false}
                  loading={
                    productData.hardCopyId === loading.id && loading.value
                  }
                  disabled={
                    productData.hardCopyId === loading.id && loading.value
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddDataToCart({
                      id: productData.hardCopyId as number,
                      temp_user_id: localUserId,
                      quantity: 1,
                      trackingData: {
                        productData: {
                          price: Number(productData.softCopyPrice),
                          id: productData.hardCopyId as number,
                          heading: productData.heading as string,
                          quantity: 1,
                          currency: productData?.i18n_attributes
                            ?.currency as string,
                        },
                        userData,
                        pathname: router.pathname,
                        productType: 'hardcopy',
                        type: 'courses',
                      },
                    });
                  }}
                  className=' rounded-lg bg-white flex justify-center items-center'
                >
                  <div className='pl-2 pr-2.5 py-[18px]'>
                    <ShouldRender check={productData.hardCopyId !== loading.id}>
                      <ProductCartIcon />
                    </ShouldRender>
                    <ShouldRender check={productData.hardCopyId === loading.id}>
                      <Spinner />
                    </ShouldRender>
                  </div>
                </ButtonDefault>
              </ShouldRender>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursesCard;
